import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import ProductItem from '../ProductItem'
import { Link } from 'react-router-dom'
import { convertCentsToDollar, getCategory } from '../../utils'
import config from '../../aws-exports'

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket
} = config

/* Client-side product search across every market with instant autocomplete popover and category filters. */
const ProductSearch = ({ markets, children }) => {
  const [query, setQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [showPopover, setShowPopover] = useState(false)
  const searchBoxRef = useRef(null)

  useEffect(() => {
    if (query || selectedCategory !== 'All') {
      window.scrollTo(0, 0);
    }
  }, [query, selectedCategory]);

  // Handle outside click to close popover
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const q = query.trim().toLowerCase()

  const CATEGORIES = ['All', 'Spices & Masalas', 'Grains & Dal', 'Snacks & Sweets', 'Beverages', 'Frozen & Dairy', 'Pantry & Groceries'];

  const results = (markets || []).flatMap(market =>
    (market.products && market.products.items ? market.products.items : [])
      .map(p => ({ ...p, marketName: market.name, categoryName: getCategory(p) }))
      .filter(p => {
        const matchesCategory = selectedCategory === 'All' || p.categoryName === selectedCategory;
        const matchesQuery = !q || (p.description && p.description.toLowerCase().includes(q)) || (p.categoryName && p.categoryName.toLowerCase().includes(q));
        return matchesCategory && matchesQuery;
      })
  );

  const topSuggestions = results.slice(0, 5);

  const getImgUrl = (file) => {
    if (!file || !file.key) return '';
    return `https://${bucket}.s3.${region}.amazonaws.com/public/${file.key}`;
  };

  return (
    <>
      <SearchWrap>
        <SearchBox ref={searchBoxRef}>
          <SearchIcon>
            <span role="img" aria-label="search">🔍</span>
          </SearchIcon>
          <SearchInput
            type="text"
            value={query}
            placeholder="Search products — rice, spices, tea…"
            aria-label="Search products"
            onChange={e => {
              setQuery(e.target.value);
              setShowPopover(true);
            }}
            onFocus={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setShowPopover(true);
            }}
            onKeyDown={e => {
              if (e.key === 'Escape') setShowPopover(false);
            }}
          />
          {query && (
            <ClearBtn type="button" aria-label="Clear search" onClick={() => setQuery('')}>
              ✕
            </ClearBtn>
          )}

          {/* Autocomplete Instant Suggestions Popover */}
          {showPopover && q.length > 0 && (
            <PopoverDropdown>
              <PopoverHead>
                Instant Results ({results.length})
              </PopoverHead>
              {topSuggestions.length > 0 ? (
                topSuggestions.map(item => (
                  <SuggestionItem
                    key={item.id}
                    to={`/products/${item.id}`}
                    onClick={() => setShowPopover(false)}
                  >
                    <ThumbWrap>
                      <img src={getImgUrl(item.file)} alt={item.description} />
                    </ThumbWrap>
                    <ItemMeta>
                      <ItemTitle>{item.description}</ItemTitle>
                      <ItemSub>{item.categoryName} • in {item.marketName}</ItemSub>
                    </ItemMeta>
                    <ItemPrice>${convertCentsToDollar(item.price)}</ItemPrice>
                  </SuggestionItem>
                ))
              ) : (
                <NoSuggestionMsg>No matching products found.</NoSuggestionMsg>
              )}

              {results.length > 5 && (
                <ViewAllRow onClick={() => setShowPopover(false)}>
                  See all {results.length} results →
                </ViewAllRow>
              )}
            </PopoverDropdown>
          )}
        </SearchBox>

        <CategoryFilterRow>
          {CATEGORIES.map(cat => (
            <CategoryPill
              key={cat}
              type="button"
              $active={selectedCategory === cat}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </CategoryPill>
          ))}
        </CategoryFilterRow>
      </SearchWrap>

      {q || selectedCategory !== 'All' ? (
        <ResultsWrap>
          <ResultsHead>
            <span>
              {results.length} product{results.length !== 1 ? 's' : ''} found
              {selectedCategory !== 'All' ? ` in ${selectedCategory}` : ''}
              {query.trim() ? ` matching “${query.trim()}”` : ''}
            </span>

            {(selectedCategory !== 'All' || query) && (
              <ResetFilterBtn onClick={() => { setSelectedCategory('All'); setQuery(''); }}>
                Reset Filters ✕
              </ResetFilterBtn>
            )}
          </ResultsHead>

          {results.length === 0 && (
            <EmptyHint>No items found. Try choosing &ldquo;All&rdquo; or searching a different term like &ldquo;rice&rdquo; or &ldquo;spices&rdquo;.</EmptyHint>
          )}

          {results.length > 0 && (
            <Grid>
              {results.map(product => (
                <Cell key={product.id}>
                  <ProductItem product={product} />
                  <MarketTag>in {product.marketName}</MarketTag>
                </Cell>
              ))}
            </Grid>
          )}
        </ResultsWrap>
      ) : (
        children
      )}
    </>
  )
}

export default ProductSearch

/* ---------- styles ---------- */
const SearchWrap = styled.div`
  background: #fff;
  border-bottom: 1px solid rgba(31, 45, 61, 0.07);
  padding: 10px 16px;
  position: sticky;
  top: 64px;
  z-index: 7;

  @media screen and (min-width: 768px) {
    padding: 12px 24px;
  }

  @media screen and (min-width: 900px) {
    top: 80px;
  }
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: 560px;
  margin: 0 auto;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.12);
  border-radius: 999px;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #01bf71;
    box-shadow: 0 0 0 3px rgba(1, 191, 113, 0.15);
  }
`;

const SearchIcon = styled.span`
  flex: 0 0 auto;
  padding: 0 4px 0 16px;
  font-size: 1rem;
`;

const SearchInput = styled.input`
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  padding: 13px 10px;
  font-size: 1rem;
  color: #1f2d3d;

  &::placeholder {
    color: #aab4bf;
  }
`;

const ClearBtn = styled.button`
  flex: 0 0 auto;
  margin-right: 8px;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: rgba(31, 45, 61, 0.07);
  color: #6a7b8a;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.12);
    color: #d32f2f;
  }
`;

const PopoverDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.12);
  border-radius: 16px;
  box-shadow: 0 12px 32px rgba(31, 45, 61, 0.14);
  overflow: hidden;
  z-index: 100;
  max-height: 420px;
  overflow-y: auto;
`;

const PopoverHead = styled.div`
  padding: 10px 16px;
  background: #f7f9fa;
  font-size: 0.75rem;
  font-weight: 800;
  color: #8a99a8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
`;

const SuggestionItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  text-decoration: none;
  color: inherit;
  border-bottom: 1px solid rgba(31, 45, 61, 0.04);
  transition: background 0.15s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.06);
  }
`;

const ThumbWrap = styled.div`
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 8px;
  overflow: hidden;
  background: #f7f9fa;
  display: grid;
  place-items: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const ItemMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2d3d;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemSub = styled.div`
  font-size: 0.78rem;
  color: #8a99a8;
`;

const ItemPrice = styled.div`
  font-size: 0.95rem;
  font-weight: 800;
  color: #0a8f57;
  flex: 0 0 auto;
`;

const NoSuggestionMsg = styled.div`
  padding: 18px;
  text-align: center;
  color: #8a99a8;
  font-size: 0.9rem;
`;

const ViewAllRow = styled.div`
  padding: 12px 16px;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: #0a8f57;
  background: rgba(1, 191, 113, 0.06);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.12);
  }
`;

const ResultsWrap = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 20px 16px 44px;

  @media screen and (min-width: 768px) {
    padding: 24px 24px 56px;
  }
`;

const ResultsHead = styled.h3`
  margin: 0 0 16px;
  font-size: 1.1rem;
  font-weight: 800;
  color: #1f2d3d;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

const EmptyHint = styled.p`
  margin: 0;
  color: #8a99a8;
`;

const Grid = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: 580px) {
    gap: 14px;
  }
  @media (min-width: 640px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 960px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
`;

const MarketTag = styled.span`
  font-size: 0.75rem;
  color: #8a99a8;
  margin-top: 6px;
  margin-left: 4px;
`;

const CategoryFilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 760px;
  margin: 12px auto 0;
  overflow-x: auto;
  padding: 4px 0;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategoryPill = styled.button`
  white-space: nowrap;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 0 0 auto;

  background: ${props => (props.$active ? '#01bf71' : 'rgba(255, 255, 255, 0.95)')};
  color: ${props => (props.$active ? '#fff' : '#4a5568')};
  border: 1px solid ${props => (props.$active ? '#01bf71' : 'rgba(31, 45, 61, 0.12)')};
  box-shadow: ${props => (props.$active ? '0 4px 12px rgba(1, 191, 113, 0.25)' : 'none')};

  &:hover {
    background: ${props => (props.$active ? '#01bf71' : 'rgba(1, 191, 113, 0.1)')};
    color: ${props => (props.$active ? '#fff' : '#0a8f57')};
    border-color: #01bf71;
  }
`;

const ResetFilterBtn = styled.button`
  background: rgba(239, 83, 80, 0.1);
  color: #d32f2f;
  border: none;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(239, 83, 80, 0.2);
  }
`;
